import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { type ExpandableRowConfig } from "../../types";
import { createExpandableColumn } from "../utils/createExpandableColumn";

export const useExpandableRows = <TData,>(
  data: TData[],
  config: ExpandableRowConfig<TData>,
) => {
  const hasInitializedDefaultExpansionRef = useRef(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(
    new Set(
      config.defaultExpanded
        ? data.filter(config.isExpandable).map(config.getRowId)
        : [],
    ),
  );

  // Ensure default expansion is applied when data arrives asynchronously.
  useEffect(() => {
    if (config.defaultExpanded && !hasInitializedDefaultExpansionRef.current) {
      const defaultExpandedIds = data
        .filter(config.isExpandable)
        .map(config.getRowId);

      if (defaultExpandedIds.length > 0) {
        setExpandedRows(new Set(defaultExpandedIds));
        hasInitializedDefaultExpansionRef.current = true;
      }
    }
  }, [data, config]);

  const expandedData = useMemo(() => {
    const result: TData[] = [];

    const processRow = (row: TData, level: number) => {
      const rowId = config.getRowId(row);
      const parentId = config.getParentId?.(row);

      if (level === 0 || (parentId && expandedRows.has(parentId))) {
        result.push(row);

        if (config.isExpandable(row) && expandedRows.has(rowId)) {
          const subRows = config.getSubRows(row);
          subRows.forEach((subRow) => {
            processRow(subRow, level + 1);
          });
        }
      }
    };

    data.forEach((row) => {
      if (config.getRowLevel(row) === 0) {
        processRow(row, 0);
      }
    });

    return result;
  }, [data, config, expandedRows]);

  const toggleRow = useCallback((rowId: string) => {
    hasInitializedDefaultExpansionRef.current = true;
    setExpandedRows((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }

      return newSet;
    });
  }, []);

  const isExpanded = (rowId: string) => expandedRows.has(rowId);

  const expandableColumn = useMemo(
    () => createExpandableColumn(config, expandedRows, toggleRow, data),
    [config, expandedRows, toggleRow, data],
  );

  const isExpandableToggle = (rowId: string) => {
    const row = data.find((row: TData) => config.getRowId(row) === rowId);

    return row && config.isExpandable(row);
  };

  return {
    expandedData,
    expandedRows,
    isExpanded,
    toggleRow,
    expandableColumn,
    isExpandableToggle,
  };
};
